import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowDownLeft } from 'lucide-react';
import { fetchAgentShopWallets, withdrawCreditsFromShop } from '../../services/api';
import type { AgentShopStatus } from '../../types';
import AvailableCreditsCallout from './AvailableCreditsCallout';
import { MONEY } from '@kioskgaming/ui';

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface WithdrawCreditsFromShopTriggerProps {
  shopId: string;
  shopName: string;
  shopStatus?: AgentShopStatus;
  /** Shop cost as decimal (0–1) — cash preview when present. */
  shopCostRate?: string | number;
  /** Shop available credits (source wallet). When set, shown without refetching. */
  sourceCreditBalance?: number;
  onSuccess?: () => void;
  buttonText?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export default function WithdrawCreditsFromShopTrigger({
  shopId,
  shopName,
  shopStatus,
  shopCostRate,
  sourceCreditBalance: sourceCreditBalanceProp,
  onSuccess,
  buttonText = MONEY.withdrawCredits,
  size = 'md',
  className = '',
}: WithdrawCreditsFromShopTriggerProps) {
  const [open, setOpen] = useState(false);
  const [amountStr, setAmountStr] = useState('');
  const [note, setNote] = useState('');
  const [balance, setBalance] = useState<number | null>(
    sourceCreditBalanceProp !== undefined ? sourceCreditBalanceProp : null,
  );
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (sourceCreditBalanceProp !== undefined) setBalance(sourceCreditBalanceProp);
  }, [sourceCreditBalanceProp]);

  const loadBalance = useCallback(async () => {
    if (sourceCreditBalanceProp !== undefined) {
      setBalance(sourceCreditBalanceProp);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await fetchAgentShopWallets(shopId);
      if (res.success && res.data) {
        setBalance(res.data.credit_wallet.available_balance);
      } else {
        setBalance(null);
      }
    } catch {
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, [shopId, sourceCreditBalanceProp]);

  useEffect(() => {
    if (!open) return;
    void loadBalance();
    setAmountStr('');
    setNote('');
  }, [open, loadBalance]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const effectiveCostRate = (() => {
    const n = Number(shopCostRate ?? 0);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
  })();

  const creditsPreview = parseFloat(amountStr);
  const cashPreview =
    Number.isFinite(creditsPreview) && creditsPreview > 0 ? creditsPreview * effectiveCostRate : null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid amount greater than zero');
      return;
    }
    setSubmitting(true);
    try {
      const res = await withdrawCreditsFromShop(shopId, amount, note);
      if (!res.success) {
        toast.error(res.message || 'Withdraw credits failed');
        return;
      }
      toast.success('Credits withdrawn');
      setOpen(false);
      onSuccess?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Withdraw credits failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (shopStatus === 'terminated') return null;

  const btnClass =
    size === 'sm'
      ? 'inline-flex items-center justify-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50'
      : 'inline-flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50';

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`${btnClass} ${className}`}>
        <ArrowDownLeft className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />
        {buttonText}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto p-4 sm:flex sm:items-center sm:justify-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
            aria-label="Close dialog"
            onClick={() => !submitting && setOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-withdraw-shop-title"
            className="relative z-10 w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-2xl max-h-[calc(100vh-2rem)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="agent-withdraw-shop-title" className="text-lg font-semibold text-gray-900">
              Withdraw credits from Shop
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Credits are debited from the Shop wallet and credited to your Agent balance. A payment record is
              stored for the cash settlement:{' '}
              <span className="font-medium text-gray-700">cash = credits × Shop cost</span>.
            </p>

            <form onSubmit={onSubmit} className="mt-4 space-y-4">
              <AvailableCreditsCallout balance={balance} loading={balanceLoading} />

              <div>
                <p className="text-sm font-medium text-gray-700">Shop</p>
                <p className="mt-1 text-sm text-gray-900">{shopName}</p>
              </div>

              <div>
                <label htmlFor="agent-withdraw-amount" className="mb-1 block text-sm font-medium text-gray-700">
                  Amount
                </label>
                <input
                  id="agent-withdraw-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0.01"
                  autoComplete="off"
                  value={amountStr}
                  onChange={(ev) => setAmountStr(ev.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
                />
                {cashPreview != null && (
                  <p className="mt-2 rounded-md border border-gray-100 bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
                    Credits moved: <span className="font-semibold">{formatMoney(creditsPreview)}</span>
                    <span className="mx-1 text-gray-400">·</span>
                    Cash recorded (you ↔ Shop):{' '}
                    <span className="font-semibold text-gray-900">${formatMoney(cashPreview)} USD</span>
                    <span className="text-gray-500">
                      {' '}
                      (Shop cost {(effectiveCostRate * 100).toFixed(2)}%)
                    </span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="agent-withdraw-note" className="mb-1 block text-sm font-medium text-gray-700">
                  Note
                </label>
                <textarea
                  id="agent-withdraw-note"
                  rows={3}
                  value={note}
                  onChange={(ev) => setNote(ev.target.value)}
                  placeholder="Optional note for this withdrawal"
                  maxLength={500}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                />
                <p className="mt-1 text-xs text-gray-500">Optional. Shown on the payment record when provided.</p>
              </div>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {submitting ? 'Withdrawing…' : MONEY.withdrawCredits}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

